<template>
  <div
    id="app"
    class="h-100"
    :class="[skinClasses, appClass ]"
  >
    <template v-if="isAuthenticated">
      <verify-account-alert @update:height="verificationAlertHeight = $event" />

      <subscription-status-bar @update:height="subscriptionStatusHeight = $event" />

      <help-modal />

      <setup-completed-modal />

      <add-branch-modal />

      <extra-branches-modal />

      <multi-branches-modal />

      <announcement-modal
        v-model="announcementModal"
        :announcement="announcement"
      />
    </template>

    <component
      :is="layout"
      class="main-layout"
      :style="{paddingTop: `${offset}px` }"
    >
      <router-view />
    </component>
    <portal-target
      name="body"
      multiple
    />

    <div
      class="position-absolute canvas-divs"
      style="z-index: -10; bottom: 0;"
    >
      <canvas
        id="receipt-logo-canvas"
        height="340"
        width="454"
      />
      <canvas
        id="text-canvas"
        height="32"
      />
    </div>
  </div>
</template>

<script>

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { ref, watch } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'

import { useWindowSize, useCssVar } from '@vueuse/core'

import store from '@/store'
import LocalizationService from '@/services/localization-service'
import SubscriptionStatusBar from '@/views/admin/billing/components/SubscriptionStatusBar'
import HelpModal from '@/@okmenu/components/help/HelpModal'
import SetupCompletedModal from '@/@okmenu/components/wizards/setup-wizard/SetupCompletedModal'
import AddBranchModal from '@/@okmenu/components/modals/branches/AddBranchModal'
import ExtraBranchesModal from '@/@okmenu/components/modals/branches/ExtraBranchesModal'
import MultiBranchesModal from '@/@okmenu/components/modals/branches/MultiBranchesModal'
import HotjarService from '@/services/hotjar-service'
import OneSignalService from '@/services/one-signal-service'
import VerifyAccountAlert from '@/@okmenu/components/alerts/VerifyAccountAlert'

import LayoutVertical from '@/layouts/vertical/LayoutVertical.vue'
import LayoutFull from '@/layouts/full/LayoutFull.vue'
import AnnouncementModal from '@/views/superadmin/dashboard/announcements/components/AnnouncementModal'

export default {
  provide() {
    const config = {}

    Object.defineProperty(config, 'isDark', {
      enumerable: true,
      get: () => this.isDark,
    })

    Object.defineProperty(config, 'isRTL', {
      enumerable: true,
      get: () => this.isRTL,
    })

    return {
      config,
      getTranslatedInfo: LocalizationService.getTranslate,
    }
  },
  components: {
    AnnouncementModal,
    VerifyAccountAlert,
    MultiBranchesModal,
    ExtraBranchesModal,
    AddBranchModal,
    SetupCompletedModal,
    HelpModal,
    SubscriptionStatusBar,

    // Layouts
    //  LayoutHorizontal,
    LayoutVertical,
    LayoutFull,

  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    isAuthenticated() {
      return this.$store.getters['auth/isAuthenticated']
    },
    unreadAnnouncement() {
      return this.$store.getters['auth/unreadAnnouncement']
    },
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
    offset() {
      return this.subscriptionStatusHeight + this.verificationAlertHeight
    },
    appClass() {
      if (this.subscriptionStatusHeight && this.verificationAlertHeight) return 'verification-subscription-status-visible'
      if (this.subscriptionStatusHeight || this.verificationAlertHeight) return 'subscription-status-visible'
      return ''
    },
  },
  watch: {
    unreadAnnouncement() {
      if (this.isAuthenticated && this.unreadAnnouncement) {
        this.announcement = this.unreadAnnouncement
        this.announcementModal = true
      }
    },
  },
  beforeCreate() {
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  setup() {
    const { skin, skinClasses } = useAppConfig()
    const subscriptionStatusHeight = ref(0)
    const verificationAlertHeight = ref(0)
    const announcementModal = ref(false)
    const announcement = ref(null)

    LocalizationService.initLocale()
    LocalizationService.updateDirection(LocalizationService.getCurrentLocale())

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      // timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses,
      subscriptionStatusHeight,
      verificationAlertHeight,
      announcementModal,
      announcement,
    }
  },
  mounted() {
    HotjarService.init()
    OneSignalService.initOneSignal()

    if (this.isAuthenticated && this.unreadAnnouncement) {
      this.announcement = this.unreadAnnouncement
      this.announcementModal = true
    }
  },
  metaInfo() {
    return {
      title: this.$t('navigation.dashboard'),
      titleTemplate: `%s | ${this.$i18n.locale === 'ar' ? 'وي-شوب' : 'WiSHOP'}`,
    }
  },
}
</script>

<style lang="scss">
#app.my-menu-full-screen .main-layout {
  padding-top: 0!important;
}
.canvas-divs{
  left:-10000px;
  [dir=rtl] & {
    left:unset;
    right:-10000px;
  }
}
</style>
